import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Download, CheckCircle2, AlertTriangle, Loader2, Handshake, MessageSquareText } from "lucide-react";
import Footer from "@/components/enea/Footer";
import VideoGuide from "@/components/enea/VideoGuide";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BUCHI_NERI = [
  { t: "Prezzi di richiesta, non di rogito", d: "I portali stimano sugli asking price, gonfiati del 15–35% rispetto al prezzo di compravendita reale. Il cliente si innamora di un numero che non esiste." },
  { t: "Errore strutturale degli AVM: 10–20%", d: "I modelli automatici dichiarano margini di errore fino al 20%. Su un immobile da €250.000 sono €50.000 di illusione." },
  { t: "Zone troppo larghe", d: "Medie comunali o macro-zone: la media di Padova non è il prezzo dell'Arcella né del centro storico. Via per via, i valori cambiano drasticamente." },
  { t: "Zero sopralluogo = zero malus reali", d: "Nessun algoritmo di portale conta piano, ascensore, condizione delle facciate, stato interno. Le voci che in trattativa spostano il 10–20% del valore." },
  { t: "Lead generation mascherata", d: "Le stime «gratuite» servono a vendere il contatto: il numero tende ad accontentare il proprietario per farlo cliccare. È strategico, non tecnico." },
  { t: "Dati vecchi di mesi", d: "Basi dati aggiornate con ritardi di 3–6 mesi, fonte non dichiarata. In un mercato che gira, è guidare guardando lo specchietto." },
];

const SOLUZIONI = [
  { t: "Quotazioni OMI ufficiali min–max, sempre dichiarate", d: "Range da quotazioni reali dell'Agenzia delle Entrate per comune e fascia, aggiornamento semestrale automatico. Fonte citata su ogni report." },
  { t: "Coefficienti estimativi veri (prassi IVS / Tecnoborsa)", d: "Taglio per mq, piano e ascensore, garage a corpo con quotazioni OMI Box, stato conservativo in 8 livelli, condizione esterna del fabbricato." },
  { t: "Rischio di zona calcolato, non inventato", d: "Mesi reali stimati di vendita nella zona e probabilità di proposte vincolate al mutuo. Il venditore capisce da solo che «aspettare l'amatore» costa." },
  { t: "Filtro d'ingresso: lead pre-qualificate", d: "Obiettivo, tolleranza al rischio e intenzione di vendita prima della stima. Le lead calde (vende subito / 3 mesi) sono segnalate in tempo reale." },
  { t: "La doccia fredda è nel report, non nel tuo ufficio", d: "Il PDF dice al proprietario ciò che nessun portale ha il coraggio di dirgli. Tu raccogli un venditore già ancorato alla realtà." },
];

const CONFRONTO = [
  ["Fonte €/mq", "OMI ufficiale AdE (min–max, citata)", "Asking price annunci", "Mista, non dichiarata"],
  ["Malus reali (piano, ascensore, esterno)", "Sì, coefficienti estimativi", "No", "Parziale"],
  ["Rischio di zona (mesi, vincolate)", "Sì, calcolato per zona", "No", "No"],
  ["Qualificazione della lead", "Obiettivo + rischio + intenzione", "Nessuna", "Nessuna"],
  ["Report PDF con nota metodologica", "Sì, 12 pagine", "No", "Raro"],
  ["A chi serve la stima", "Al venditore (verità)", "Al portale (contatti)", "Alla banca (prudenza)"],
];

const EMPTY = { agency_name: "", contact_name: "", email: "", phone: "", city: "", source: "partnership", message: "" };

export default function Agenzie() {
  const [form, setForm] = useState(EMPTY);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.agency_name.trim().length < 2) return toast.error("Inserisci il nome dell'agenzia.");
    if (form.contact_name.trim().length < 2) return toast.error("Inserisci il nome del referente.");
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) return toast.error("Inserisci un'email valida.");
    setSending(true);
    try {
      await axios.post(`${API}/agency-lead`, { ...form, phone: form.phone || null, city: form.city || null, message: form.message || null });
      setSent(true);
    } catch {
      toast.error("Invio non riuscito, riprova tra qualche minuto.");
    } finally {
      setSending(false);
    }
  };

  const inputCls = "w-full px-3.5 py-2.5 rounded-xl bg-white/5 border border-white/15 text-white placeholder-slate-500 text-sm focus:outline-none focus:border-teal-400 transition-colors";
  const labelCls = "block text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-1.5";

  return (
    <div className="min-h-screen bg-[#081c33] text-white" data-testid="agenzie-page">
      <header className="sticky top-0 z-40 bg-[#081c33]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 text-sm text-slate-300 hover:text-white transition-colors" data-testid="agenzie-back-home">
            <ArrowLeft className="w-4 h-4" /> Torna alla home
          </a>
          <a href={`${API}/brochure-agenzie`} className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-teal-500 hover:bg-teal-400 text-[#081c33] text-sm font-bold transition-colors" data-testid="agenzie-brochure-top">
            <Download className="w-4 h-4" /> Brochure PDF
          </a>
        </div>
      </header>

      {/* HERO */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-14 pb-10">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">Per le agenzie immobiliari · Padova · Veneto · Italia</div>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05]" data-testid="agenzie-hero-title">
          Spietata. Precisa.<br /><span className="text-teal-300">Affidabile.</span>
        </h1>
        <p className="mt-5 max-w-2xl text-base text-slate-300 leading-relaxed">
          ENEA Specialist parla la lingua degli agenti immobiliari — non quella del venditore convinto di avere
          sempre e comunque l&apos;oro in mano. La doccia fredda sul prezzo la fa il report, con fonti ufficiali: tu incontri un venditore già ancorato alla realtà.
        </p>
        <div className="mt-7 flex flex-wrap gap-3">
          <a href="#partnership" className="px-6 py-3 rounded-full bg-teal-500 hover:bg-teal-400 text-[#081c33] text-sm font-bold transition-colors" data-testid="agenzie-cta-partnership">Richiedi una partnership</a>
          <a href={`${API}/brochure-agenzie`} className="px-6 py-3 rounded-full border border-white/25 hover:border-teal-300 text-sm font-semibold transition-colors inline-flex items-center gap-2" data-testid="agenzie-brochure-hero">
            <Download className="w-4 h-4" /> Scarica la brochure
          </a>
        </div>
      </section>

      {/* VIDEO GUIDA */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 pb-12" data-testid="agenzie-video-section">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">Il funnel in 60 secondi</div>
        <h2 className="mt-2 text-lg font-bold">Guarda cosa vede il venditore prima di arrivare da te</h2>
        <div className="mt-5 max-w-3xl">
          <VideoGuide page="agenzie" />
        </div>
      </section>

      {/* BUCHI NERI */}
      <section className="border-t border-white/10 bg-[#0b2545]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-amber-400">01 · I buchi neri degli altri sistemi</div>
          <h2 className="mt-2 text-lg font-bold">Perché le valutazioni online tradiscono chi vende (e chi vende per lui)</h2>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="agenzie-buchi-neri">
            {BUCHI_NERI.map((b, i) => (
              <div key={b.t} className="rounded-xl border border-amber-400/20 bg-white/[0.03] p-4">
                <div className="flex items-start gap-2">
                  <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
                  <div>
                    <div className="text-sm font-bold text-amber-200">{i + 1}. {b.t}</div>
                    <p className="mt-1.5 text-xs text-slate-300 leading-relaxed">{b.d}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
          <p className="mt-5 text-sm font-semibold text-amber-300">Risultato: incarichi fuori prezzo, annunci che invecchiano, venditori che «aspettano l&apos;amatore». Il costo lo paga l&apos;agente.</p>
        </div>
      </section>

      {/* SOLUZIONI */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
        <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">02 · Come ENEA chiude i buchi</div>
        <h2 className="mt-2 text-lg font-bold">Numeri difendibili, non promesse</h2>
        <div className="mt-6 space-y-3" data-testid="agenzie-soluzioni">
          {SOLUZIONI.map((s) => (
            <div key={s.t} className="flex items-start gap-3 rounded-xl border border-white/10 bg-white/[0.03] p-4">
              <CheckCircle2 className="w-4 h-4 text-teal-300 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-white">{s.t}</div>
                <p className="mt-1 text-xs text-slate-300 leading-relaxed">{s.d}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CONFRONTO */}
      <section className="border-t border-white/10 bg-[#0b2545]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-12">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">03 · Confronto diretto</div>
          <div className="mt-5 overflow-x-auto rounded-xl border border-white/10" data-testid="agenzie-confronto">
            <table className="w-full text-xs min-w-[640px]">
              <thead>
                <tr className="text-left text-[10px] uppercase tracking-wider text-slate-300 bg-white/5">
                  <th className="px-4 py-3"></th>
                  <th className="px-4 py-3 text-teal-300">ENEA Specialist</th>
                  <th className="px-4 py-3">Portali annunci</th>
                  <th className="px-4 py-3">AVM generici</th>
                </tr>
              </thead>
              <tbody>
                {CONFRONTO.map((row) => (
                  <tr key={row[0]} className="border-t border-white/10">
                    <td className="px-4 py-2.5 font-semibold text-slate-200">{row[0]}</td>
                    <td className="px-4 py-2.5 text-teal-200 bg-teal-400/[0.06] font-medium">{row[1]}</td>
                    <td className="px-4 py-2.5 text-slate-400">{row[2]}</td>
                    <td className="px-4 py-2.5 text-slate-400">{row[3]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </section>

      {/* FORM */}
      <section id="partnership" className="max-w-5xl mx-auto px-4 sm:px-6 py-14">
        <div className="max-w-2xl">
          <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-teal-300">04 · Parliamone</div>
          <h2 className="mt-2 text-lg font-bold">Partnership o feedback sul funnel: scrivici</h2>
          <p className="mt-2 text-sm text-slate-300">Hai testato ENEA Specialist o vuoi una convenzione per la tua agenzia? Il team Gruppo Vela ti risponde entro 24 ore lavorative.</p>

          {sent ? (
            <div className="mt-6 rounded-xl border border-teal-400/40 bg-teal-400/10 p-5 flex items-start gap-3" data-testid="agenzie-form-success">
              <CheckCircle2 className="w-5 h-5 text-teal-300 flex-shrink-0 mt-0.5" />
              <div>
                <div className="text-sm font-bold text-teal-200">Richiesta inviata!</div>
                <p className="mt-1 text-xs text-slate-300">Grazie {form.contact_name.split(" ")[0]}: il team ti ricontatta a breve su {form.email}.</p>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="agenzie-form">
              <div className="flex gap-2">
                {[{ id: "partnership", label: "Voglio una partnership", Icon: Handshake }, { id: "feedback", label: "Ho un feedback sul funnel", Icon: MessageSquareText }].map(({ id, label, Icon }) => (
                  <button key={id} type="button" onClick={() => setF({ source: id })}
                    className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold border transition-colors ${form.source === id ? "bg-teal-500 text-[#081c33] border-teal-500" : "border-white/20 text-slate-300 hover:border-teal-300"}`}
                    data-testid={`agenzie-source-${id}`}>
                    <Icon className="w-3.5 h-3.5" /> {label}
                  </button>
                ))}
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className={labelCls}>Nome agenzia *</label>
                  <input value={form.agency_name} onChange={(e) => setF({ agency_name: e.target.value })} placeholder="Es. Immobiliare Rossi" className={inputCls} data-testid="agenzie-input-agency" />
                </div>
                <div>
                  <label className={labelCls}>Referente *</label>
                  <input value={form.contact_name} onChange={(e) => setF({ contact_name: e.target.value })} placeholder="Nome e cognome" className={inputCls} data-testid="agenzie-input-contact" />
                </div>
                <div>
                  <label className={labelCls}>Email *</label>
                  <input type="email" value={form.email} onChange={(e) => setF({ email: e.target.value })} placeholder="nome@agenzia.it" className={inputCls} data-testid="agenzie-input-email" />
                </div>
                <div>
                  <label className={labelCls}>Telefono</label>
                  <input value={form.phone} onChange={(e) => setF({ phone: e.target.value })} placeholder="Opzionale" className={inputCls} data-testid="agenzie-input-phone" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>Città / zona operativa</label>
                  <input value={form.city} onChange={(e) => setF({ city: e.target.value })} placeholder="Es. Padova e provincia" className={inputCls} data-testid="agenzie-input-city" />
                </div>
                <div className="sm:col-span-2">
                  <label className={labelCls}>{form.source === "feedback" ? "Il tuo feedback sul funnel" : "Messaggio"}</label>
                  <textarea rows={4} value={form.message} onChange={(e) => setF({ message: e.target.value })}
                    placeholder={form.source === "feedback" ? "Cosa funziona, cosa manca, cosa cambieresti…" : "Raccontaci la tua agenzia e cosa cerchi…"}
                    className={inputCls} data-testid="agenzie-input-message" />
                </div>
              </div>
              <button type="submit" disabled={sending}
                className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-teal-500 hover:bg-teal-400 disabled:opacity-60 text-[#081c33] text-sm font-bold transition-colors"
                data-testid="agenzie-form-submit">
                {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : null} Invia la richiesta
              </button>
            </form>
          )}
        </div>
      </section>

      <Footer />
    </div>
  );
}
